import * as React from 'react';
import {
  BotIcon,
  ChartColumn,
  CreditCard,
  ImagesIcon,
  LayoutDashboard,
  LifeBuoy,
  LogOut,
  Megaphone,
  Send,
  Settings2,
  Users,
} from 'lucide-react';

import { NavMain } from '../../components/molecules/nav-main';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
} from '../../components/ui/sidebar';
import { Link, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { routes } from '@/routing/routes';
import { useTenantTheme } from '@/hooks/use-tenant-theme';
import { useAuth } from '@/hooks/use-auth';
import { useBusinessPermissions } from '@/hooks/use-business-permissions';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { useTenantCurrency } from '@/hooks/use-tenant-currency';
import { formatCurrency } from '@/utils/helpers';

export const AppSidebar: React.FC = ({
  ...props
}: React.ComponentProps<typeof Sidebar>) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { tenant } = useTenantTheme();
  const currency = useTenantCurrency();
  const { can } = useBusinessPermissions();
  const canViewBilling = can('billing:view');
  const [isLogoutDialogOpen, setIsLogoutDialogOpen] = React.useState(false);
  const tenantName = tenant?.name ?? 'PathIQ';
  const tenantLogo = tenant?.logoUrl ?? null;

  const handleConfirmLogout = React.useCallback(() => {
    logout();
    setIsLogoutDialogOpen(false);
    navigate(routes.login, { replace: true });
  }, [logout, navigate]);

  const data = React.useMemo(
    () => ({
      user: {
        name: 'User',
        email: 'm@example.com',
        avatar: '#',
      },
      navMain: [
        {
          title: t('sidebar.dashboard'),
          url: routes.dashboard,
          icon: LayoutDashboard,
          isActive: true,
        },
        {
          title: 'Ai CoPilot',
          url: routes.aiCopilot,
          icon: BotIcon,
        },
        {
          title: t('sidebar.campaigns'),
          url: routes.campaign,
          icon: Megaphone,
        },
        // Shown to postpaid advertisers too: their administrator funds a
        // wallet of theirs, and this page is where they see that balance.
        // Hidden only from members whose role has no billing:view (campaign
        // managers, analysts), for whom every billing call would be refused.
        ...(canViewBilling
          ? [
              {
                title: t('sidebar.paymentManagement'),
                url: routes.payments,
                icon: CreditCard,
              },
            ]
          : []),
        {
          title: t('sidebar.analytics'),
          url: routes.analytics,
          icon: ChartColumn,
        },
        {
          title: t('sidebar.audiences'),
          url: routes.audiences,
          icon: Users,
        },
        {
          title: t('sidebar.creativeSuite'),
          url: '/creative-suite',
          icon: ImagesIcon,
        },
        {
          title: t('sidebar.accountSettings'),
          url: '/settings',
          icon: Settings2,
        },
      ],
      navSecondary: [
        {
          title: t('sidebar.activeCampaigns'),
          url: '#',
          icon: LifeBuoy,
          value: '12',
          color: 'text-emerald-600',
        },
        {
          title: t('sidebar.totalSpend'),
          url: '#',
          icon: Send,
          value: formatCurrency(1200, currency),
          color: 'text-slate-900',
        },
        {
          title: t('sidebar.thisMonth'),
          url: '#',
          icon: Send,
          value: formatCurrency(300, currency),
          color: 'text-blue-600',
        },
      ],
    }),
    [t, currency, canViewBilling]
  );
  return (
    <Sidebar
      variant="inset"
      collapsible="icon"
      className="w-64 p-0 border-r bg-primary border-slate-200"
      {...props}
    >
      <SidebarHeader className="p-4 border-b border-secondary-800 bg-gradient-main">
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              size="lg"
              asChild
              className="hover:bg-transparent group-data-[collapsible=icon]:justify-center"
              tooltip={tenantName}
            >
              <Link
                to="/"
                className="flex items-center gap-3 focus-visible:bg-transparent"
              >
                <img
                  src={tenantLogo ?? '/images/png/pathiq-logo.png'}
                  alt={tenantName}
                  className="shrink-0 group-data-[collapsible=icon]:hidden h-12"
                />
                {/* <img
                  src={tenantLogo ?? '/images/svg/logo-icon.svg'}
                  alt={tenantName}
                  className="shrink-0 hidden group-data-[collapsible=icon]:block"
                /> */}
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent className="flex-1 py-4 pl-2 overflow-y-auto">
        <NavMain items={data.navMain} />
      </SidebarContent>
      <SidebarFooter className="p-2 border-t-[0.4px] border-t-secondary-800">
        <SidebarMenu className="pl-2">
          <SidebarMenuItem>
            <SidebarMenuButton
              onClick={() => setIsLogoutDialogOpen(true)}
              className="font-semibold transition-all duration-200 rounded-md px-3 py-4 text-base group-data-[collapsible=icon]:justify-center text-white hover:bg-gradient-main hover:text-white"
            >
              <LogOut className="shrink-0" />
              <span className="group-data-[collapsible=icon]:hidden">
                {t('navigation.logout')}
              </span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
      <ConfirmDialog
        open={isLogoutDialogOpen}
        onOpenChange={setIsLogoutDialogOpen}
        title={t('sidebar.logoutConfirmTitle')}
        description={t('sidebar.logoutConfirmDescription')}
        confirmLabel={t('navigation.logout')}
        cancelLabel={t('common.cancel')}
        onConfirm={handleConfirmLogout}
      />
      <SidebarRail />
    </Sidebar>
  );
};
